import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, ArrowUpCircle, ArrowDownCircle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function TransactionModal({ open, onClose, onSaved, structures, categories, editing }) {
  const [type, setType] = useState("income");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [structureId, setStructureId] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      if (editing) {
        setType(editing.type);
        setAmount(String(editing.amount));
        setDate(editing.date);
        setStructureId(editing.structure_id);
        setCategory(editing.category);
        setDescription(editing.description || "");
      } else {
        setType("income");
        setAmount("");
        setDate(new Date().toISOString().slice(0, 10));
        setStructureId(structures[0]?.id || "");
        setCategory("");
        setDescription("");
      }
    }
  }, [open, editing, structures]);

  const catList = type === "income" ? categories.income : categories.expense;

  const save = async () => {
    if (!amount || !structureId || !category) {
      toast.error("Compila importo, struttura e categoria");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        type,
        amount: parseFloat(amount),
        date,
        structure_id: structureId,
        category,
        description,
      };
      if (editing) await api.put(`/transactions/${editing.id}`, payload);
      else await api.post("/transactions", payload);
      toast.success(editing ? "Transazione aggiornata" : "Transazione aggiunta");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            data-testid="modal-add-transaction"
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="bg-[#111A2E] border border-white/15 rounded-2xl p-6 shadow-2xl max-w-lg w-full text-slate-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-bold">
                {editing ? "Modifica Transazione" : "Nuova Transazione"}
              </h3>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-100" data-testid="btn-close-modal">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* type toggle */}
            <div className="grid grid-cols-2 gap-3 mb-4" data-testid="input-transaction-type">
              <button
                data-testid="btn-type-income"
                onClick={() => { setType("income"); setCategory(""); }}
                className={`flex items-center justify-center gap-2 py-2.5 rounded-lg border font-medium text-sm transition-all ${
                  type === "income"
                    ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-400"
                    : "bg-[#0E1524] border-white/10 text-slate-400 hover:text-slate-200"
                }`}
              >
                <ArrowUpCircle className="w-4 h-4" /> Entrata
              </button>
              <button
                data-testid="btn-type-expense"
                onClick={() => { setType("expense"); setCategory(""); }}
                className={`flex items-center justify-center gap-2 py-2.5 rounded-lg border font-medium text-sm transition-all ${
                  type === "expense"
                    ? "bg-rose-500/15 border-rose-500/40 text-rose-400"
                    : "bg-[#0E1524] border-white/10 text-slate-400 hover:text-slate-200"
                }`}
              >
                <ArrowDownCircle className="w-4 h-4" /> Uscita
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Importo (€)</label>
                  <input
                    data-testid="input-transaction-amount"
                    type="number"
                    step="0.01"
                    className="mt-1.5 w-full bg-[#0E1524] border border-white/15 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm font-mono"
                    placeholder="0,00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Data</label>
                  <input
                    data-testid="input-transaction-date"
                    type="date"
                    className="mt-1.5 w-full bg-[#0E1524] border border-white/15 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Struttura B&B</label>
                <select
                  data-testid="input-transaction-structure"
                  className="mt-1.5 w-full bg-[#0E1524] border border-white/15 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm cursor-pointer"
                  value={structureId}
                  onChange={(e) => setStructureId(e.target.value)}
                >
                  <option value="">Seleziona struttura</option>
                  {structures.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Categoria</label>
                <select
                  data-testid="input-transaction-category"
                  className="mt-1.5 w-full bg-[#0E1524] border border-white/15 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm cursor-pointer"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="">Seleziona categoria</option>
                  {catList.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Descrizione / Note</label>
                <input
                  data-testid="input-transaction-description"
                  className="mt-1.5 w-full bg-[#0E1524] border border-white/15 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm placeholder:text-slate-500"
                  placeholder="Es. Soggiorno famiglia Bianchi"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={onClose}
                className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-100 font-medium px-4 py-2.5 rounded-lg border border-white/10 transition-all"
              >
                Annulla
              </button>
              <button
                data-testid="btn-save-transaction"
                onClick={save}
                disabled={busy}
                className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-2.5 rounded-lg transition-all active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Salva
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
